/**
 * 认证模块 API 接口
 *
 * 仅包含终端用户的钱包登录 / 令牌刷新流程。
 * 管理员认证（/admin/auth/*）相关接口不属于本 console 职责，已移除。
 */

import { post } from '../client';
import type { ApiResult } from '../types/common';
import type {
  ChallengeRequest,
  ChallengeResult,
  VerifyRequest,
  VerifyResult,
  RefreshRequest,
  RefreshResult,
} from './auth';

/**
 * 【用户】请求登录挑战
 *
 * 用户登录第一步：提交 EVM 钱包地址，获取需签名的挑战消息（nonce）。
 * 后续步骤：使用钱包私钥（eth_sign 或 personal_sign）对返回的 message 进行签名，
 * 然后携带签名调用 verifyAuth。
 *
 * @param data - 挑战请求参数
 * @param data.walletAddress - 用户的 EVM 钱包地址
 * @returns 挑战结果，包含 nonce 和需要签名的消息
 *
 * @example
 * ```ts
 * const result = await requestChallenge({ walletAddress: '0x...' });
 * if (result.ok) {
 *   const { nonce, message } = result.data;
 *   // 使用钱包签名 message
 * }
 * ```
 */
export async function requestChallenge(
  data: ChallengeRequest
): Promise<ApiResult<ChallengeResult>> {
  return post<ChallengeResult>('/auth/challenge', data);
}

/**
 * 【用户】验证钱包签名并登录
 *
 * 用户登录第二步：提交钱包地址和对挑战消息的签名，验证通过后返回 JWT 令牌。
 *
 * @param data - 验证请求参数
 * @param data.walletAddress - 用户的 EVM 钱包地址（与请求挑战时一致）
 * @param data.signature - 使用钱包私钥对挑战消息签名后的十六进制字符串
 * @returns 验证结果，包含 accessToken、refreshToken 和用户信息
 *
 * @example
 * ```ts
 * const result = await verifyAuth({
 *   walletAddress: '0x...',
 *   signature: '0x...'
 * });
 * if (result.ok) {
 *   const { accessToken, refreshToken, userId } = result.data;
 *   // 保存 token
 * }
 * ```
 */
export async function verifyAuth(
  data: VerifyRequest
): Promise<ApiResult<VerifyResult>> {
  return post<VerifyResult>('/auth/verify', data);
}

/**
 * 【用户】刷新访问令牌
 *
 * 当 accessToken 过期时，使用 refreshToken 获取新的访问令牌，无需重新登录。
 *
 * @param data - 刷新请求参数
 * @param data.refreshToken - 通过 verifyAuth 获取的刷新令牌
 * @returns 刷新结果，包含新的 accessToken
 *
 * @example
 * ```ts
 * const result = await refreshAuth({ refreshToken });
 * if (result.ok) {
 *   const { accessToken } = result.data;
 *   // 更新 accessToken
 * }
 * ```
 */
export async function refreshAuth(
  data: RefreshRequest
): Promise<ApiResult<RefreshResult>> {
  return post<RefreshResult>('/auth/refresh', data);
}
